import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiDollarSign, FiDownload, FiPrinter, FiSearch, FiTrendingUp,
  FiTrendingDown, FiActivity, FiClock, FiTarget, FiCalendar
} from 'react-icons/fi';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { colors } from '../../theme/colors';
import { TRAN_TYPE_ROUTES } from '../../utils/navigationHelper';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import { formatCurrency, formatNumber, formatDate } from '../../utils/formatters';
import useReportMemory from '../../hooks/useReportMemory';
import { addPrintBrand } from '../../utils/printBranding';

const CHART_COLORS = [
  colors.chart.primary,
  colors.chart.secondary,
  colors.chart.tertiary,
  colors.chart.accent1,
  colors.chart.accent2,
  colors.chart.accent3
];

const PRIORITY_CONFIG = {
  high: { color: colors.error.main, bg: '#FFEBEE', label: 'High' },
  medium: { color: colors.warning.main, bg: '#FFF3E0', label: 'Medium' },
  low: { color: colors.success.main, bg: '#E8F5E9', label: 'Low' }
};

const formatAmount = (amount) => {
  const dp = (typeof window !== 'undefined' && Number.isFinite(Number(window.__AMAL_DECIMAL_PLACES__)))
    ? Number(window.__AMAL_DECIMAL_PLACES__) : 2;
  if (!amount && amount !== 0) return dp > 0 ? '0.' + '0'.repeat(dp) : '0';
  return Number(amount).toLocaleString('en-PK', {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp
  });
};

const formatDateStr = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-PK', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getDaysOverdue = (dueDate) => {
  if (!dueDate) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diff = Math.floor((today - due) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
};

const getAge = (docDate) => {
  if (!docDate) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const doc = new Date(docDate);
  doc.setHours(0, 0, 0, 0);
  const diff = Math.floor((today - doc) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 0;
};

const getPriority = (daysOverdue, amount) => {
  if (daysOverdue > 30 || amount > 500000) return 'high';
  if (daysOverdue > 15 || amount > 100000) return 'medium';
  return 'low';
};

const CashFlowManagementReport = () => {
  const navigate = useNavigate();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('cash-flow-management');

  // Open the underlying invoice behind a payable/receivable row.
  const handleDocClick = (row) => {
    const route = TRAN_TYPE_ROUTES[row.tranTypeCode];
    if (route && row.id) navigate(route, { state: { viewTransactionId: row.id } });
  };

  // Data states
  const [subTransactions, setSubTransactions] = useState(() => savedMem?.subTransactions ?? []);
  const [transactions, setTransactions] = useState(() => savedMem?.transactions ?? []);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);

  // Filter states
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [selectedPriority, setSelectedPriority] = useState(() => savedMem?.selectedPriority ?? 'All');

  // UI states
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(() => savedMem?.hasSearched ?? false);
  const [payablesSortField, setPayablesSortField] = useState(() => savedMem?.payablesSortField ?? 'dueDate');
  const [payablesSortDirection, setPayablesSortDirection] = useState(() => savedMem?.payablesSortDirection ?? 'asc');
  const [receivablesSortField, setReceivablesSortField] = useState(() => savedMem?.receivablesSortField ?? 'docDate');
  const [receivablesSortDirection, setReceivablesSortDirection] = useState(() => savedMem?.receivablesSortDirection ?? 'desc');

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      // Fresh visit only — a restored snapshot keeps the user's chosen year.
      if (!savedMem) {
        if (activeFinancialYear?.id) {
          setSelectedFYId(activeFinancialYear.id);
        } else if (fys.length > 0) {
          setSelectedFYId(fys[0].id);
        }
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear, savedMem]);

  // Load customers
  const loadCustomers = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const custRef = collection(db, 'companies', activeCompany.id, 'customers');
      const q = query(custRef, orderBy('companyName', 'asc'));
      const snapshot = await getDocs(q);
      setCustomers(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  }, [activeCompany]);

  // Load vendors
  const loadVendors = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const vendRef = collection(db, 'companies', activeCompany.id, 'vendors');
      const q = query(vendRef, orderBy('vendorName', 'asc'));
      const snapshot = await getDocs(q);
      setVendors(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadFinancialYears();
    loadCustomers();
    loadVendors();

    // Set default date range — fresh visit only; Back-restore keeps saved dates.
    if (!savedMem) {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(firstDay.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, [loadFinancialYears, loadCustomers, loadVendors, savedMem]);

  // Load data
  const loadData = async () => {
    if (!activeCompany?.id || !selectedFYId) {
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      // Load subTransactions for cash/bank account movements
      const subTransRef = collection(db, 'companies', activeCompany.id, 'subTransactions');
      const subTransConditions = [
        where('status', '==', 'Posted'),
        where('financialYearId', '==', selectedFYId)
      ];
      const subTransQuery = query(subTransRef, ...subTransConditions);
      const subTransSnapshot = await getDocs(subTransQuery);
      let allSubTransactions = subTransSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });

      // Filter to cash/bank accounts only (codes starting with 11101 or 11102)
      allSubTransactions = allSubTransactions.filter(st => {
        const code = st.accountCode || '';
        return code.startsWith('11101') || code.startsWith('11102');
      });

      allSubTransactions.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));
      setSubTransactions(allSubTransactions);

      // Load transactions for receivables/payables (SI and PI with balanceAmount > 0)
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');
      const siConditions = [
        where('tranTypeCode', 'in', ['SI', 'NSI', 'PS', 'PI', 'NPI']),
        where('financialYearId', '==', selectedFYId)
      ];
      const transQuery = query(transRef, ...siConditions);
      // ACC-20: also bring still-open bills from earlier years (same as Aging).
      const openQuery = query(transRef,
        where('tranTypeCode', 'in', ['SI', 'NSI', 'PS', 'PI', 'NPI']),
        where('status', 'in', ['Posted', 'Partial']));
      const [transSnapshot, openSnapshot] = await Promise.all([getDocs(transQuery), getDocs(openQuery)]);
      const byId = new Map();
      [...transSnapshot.docs, ...openSnapshot.docs].forEach(d => byId.set(d.id, { id: d.id, ...d.data() }));
      let allTransactions = [...byId.values()]
        .filter(t => {
          if (!activeBranch || activeBranch.id === 'all') return true;
          return t.branchId === activeBranch.id;
        });

      // Filter for outstanding balances — part-paid invoices are outstanding too.
      allTransactions = allTransactions.filter(t =>
        (t.status === 'Posted' || t.status === 'Partial') && (Number(t.balanceAmount) || 0) > 0
      );

      allTransactions.sort((a, b) => (b.docDate || '').localeCompare(a.docDate || ''));
      setTransactions(allTransactions);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Remember this view (filters + generated rows) so Back restores it with 0
  // re-reads. The date/priority/sort filters are display-only over already-loaded
  // data, so we keep the snapshot in sync as the user tweaks them (in-memory, ~free).
  useEffect(() => {
    if (!hasSearched) return;
    saveMemory({
      subTransactions, transactions,
      hasSearched: true,
      selectedFYId, dateFrom, dateTo, selectedPriority,
      payablesSortField, payablesSortDirection,
      receivablesSortField, receivablesSortDirection
    });
  }, [hasSearched, subTransactions, transactions, selectedFYId, dateFrom, dateTo,
      selectedPriority, payablesSortField, payablesSortDirection,
      receivablesSortField, receivablesSortDirection, saveMemory]);

  // Filtered subTransactions by date
  const filteredSubTransactions = useMemo(() => {
    let filtered = [...subTransactions];
    if (dateFrom) {
      filtered = filtered.filter(t => (t.docDate || '') >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(t => (t.docDate || '') <= dateTo);
    }
    return filtered;
  }, [subTransactions, dateFrom, dateTo]);

  // Summary calculations
  const summary = useMemo(() => {
    // Opening balance: OB entries (debit - credit)
    const obEntries = subTransactions.filter(st => st.tranTypeCode === 'OB');
    const openingCash = obEntries.reduce((sum, st) => {
      return sum + (Number(st.debitAmount) || 0) - (Number(st.creditAmount) || 0);
    }, 0);

    // Receipts: CR + BR entries within date range (debit - credit)
    const receiptEntries = filteredSubTransactions.filter(st =>
      ['CR', 'BR'].includes(st.tranTypeCode)
    );
    const totalInflows = receiptEntries.reduce((sum, st) => {
      return sum + (Number(st.debitAmount) || 0) - (Number(st.creditAmount) || 0);
    }, 0);

    // Payments: CP + BP entries within date range (debit - credit, will be negative)
    const paymentEntries = filteredSubTransactions.filter(st =>
      ['CP', 'BP'].includes(st.tranTypeCode)
    );
    const totalOutflows = paymentEntries.reduce((sum, st) => {
      return sum + (Number(st.debitAmount) || 0) - (Number(st.creditAmount) || 0);
    }, 0);

    // Other entries within date range (not OB, CR, BR, CP, BP)
    const otherEntries = filteredSubTransactions.filter(st =>
      !['OB', 'CR', 'BR', 'CP', 'BP'].includes(st.tranTypeCode)
    );
    const otherMovements = otherEntries.reduce((sum, st) => {
      return sum + (Number(st.debitAmount) || 0) - (Number(st.creditAmount) || 0);
    }, 0);

    // Closing balance
    const closingCash = openingCash + totalInflows + totalOutflows + otherMovements;

    // DSO calculation: (totalAR / totalRevenue) * 365
    const siTransactions = transactions.filter(t => ['SI', 'NSI', 'PS'].includes(t.tranTypeCode));
    const totalAR = siTransactions.reduce((sum, t) => sum + (Number(t.balanceAmount) || 0), 0);

    // Total revenue from all SI/PS subTransactions in date range
    const siSubTrans = filteredSubTransactions.filter(st => ['SI', 'NSI', 'PS', 'CR', 'BR'].includes(st.tranTypeCode));
    const totalRevenue = receiptEntries.reduce((sum, st) => sum + Math.abs((Number(st.debitAmount) || 0) - (Number(st.creditAmount) || 0)), 0) || 1;
    const dso = totalRevenue > 0 ? (totalAR / totalRevenue) * 365 : 0;

    // 30-Day Forecast: based on average daily cash flow
    const daySpan = dateFrom && dateTo
      ? Math.max(1, Math.ceil((new Date(dateTo) - new Date(dateFrom)) / (1000 * 60 * 60 * 24)))
      : 30;
    const avgDailyFlow = (totalInflows + totalOutflows + otherMovements) / daySpan;
    const forecast30 = closingCash + (avgDailyFlow * 30);

    return {
      openingCash,
      totalInflows,
      totalOutflows,
      closingCash,
      otherMovements,
      dso: Math.round(dso),
      forecast30,
      avgDailyFlow,
      totalAR,
      totalRevenue
    };
  }, [subTransactions, filteredSubTransactions, transactions, dateFrom, dateTo]);

  // Waterfall chart data
  const waterfallData = useMemo(() => {
    return [
      {
        name: 'Opening',
        value: summary.openingCash,
        fill: CHART_COLORS[0],
        base: 0
      },
      {
        name: 'Receipts',
        value: summary.totalInflows,
        fill: colors.success.main,
        base: summary.openingCash
      },
      {
        name: 'Payments',
        value: summary.totalOutflows,
        fill: colors.error.main,
        base: summary.openingCash + summary.totalInflows
      },
      {
        name: 'Other',
        value: summary.otherMovements,
        fill: colors.warning.main,
        base: summary.openingCash + summary.totalInflows + summary.totalOutflows
      },
      {
        name: 'Closing',
        value: summary.closingCash,
        fill: CHART_COLORS[1],
        base: 0
      }
    ];
  }, [summary]);

  // Cash Forecast AreaChart data (30/60/90 days)
  const forecastData = useMemo(() => {
    const { closingCash, avgDailyFlow } = summary;
    const data = [];
    for (let day = 0; day <= 90; day += 5) {
      data.push({
        day: `Day ${day}`,
        projected: Math.round(closingCash + (avgDailyFlow * day)),
        optimistic: Math.round(closingCash + (avgDailyFlow * 1.2 * day)),
        conservative: Math.round(closingCash + (avgDailyFlow * 0.8 * day))
      });
    }
    return data;
  }, [summary]);

  // DSO Trend (monthly DSO values from subTransactions)
  const dsoTrendData = useMemo(() => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthlyData = {};

    // Group receipt entries by month
    const receiptEntries = subTransactions.filter(st => ['CR', 'BR'].includes(st.tranTypeCode));
    receiptEntries.forEach(st => {
      if (!st.docDate) return;
      const date = new Date(st.docDate);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyData[key]) {
        monthlyData[key] = { revenue: 0 };
      }
      monthlyData[key].revenue += Math.abs((Number(st.debitAmount) || 0) - (Number(st.creditAmount) || 0));
    });

    // Get AR at each month end (simplified: use total AR proportionally)
    const totalAR = summary.totalAR;
    const keys = Object.keys(monthlyData).sort();

    return keys.map(key => {
      const [year, month] = key.split('-');
      const monthRevenue = monthlyData[key].revenue || 1;
      const monthDSO = monthRevenue > 0 ? (totalAR / Object.keys(monthlyData).length) / monthRevenue * 365 : 0;
      return {
        month: `${months[parseInt(month, 10) - 1]} ${year}`,
        dso: Math.round(Math.min(monthDSO, 365)),
        target: 30
      };
    });
  }, [subTransactions, summary.totalAR]);

  // Upcoming Payables (PI transactions with balance > 0)
  const payablesData = useMemo(() => {
    const piTransactions = transactions.filter(t => ['PI', 'NPI'].includes(t.tranTypeCode));
    const vendorMap = {};
    vendors.forEach(v => { vendorMap[v.id] = v.vendorName || v.vendorCode || 'Unknown'; });

    let payables = piTransactions.map(t => {
      const daysOverdue = getDaysOverdue(t.dueDate || t.docDate);
      const amount = Number(t.balanceAmount) || 0;
      const priority = getPriority(daysOverdue, amount);
      return {
        id: t.id,
        tranTypeCode: t.tranTypeCode,
        vendor: t.partyName || vendorMap[t.partyId] || 'Unknown',
        invoiceNo: t.docNumber || '-',
        amount,
        dueDate: t.dueDate || t.docDate || '',
        daysOverdue,
        priority
      };
    });

    // Priority filter
    if (selectedPriority !== 'All') {
      payables = payables.filter(p => p.priority === selectedPriority.toLowerCase());
    }

    return payables;
  }, [transactions, vendors, selectedPriority]);

  // Expected Receivables (SI transactions with balance > 0)
  const receivablesData = useMemo(() => {
    const siTransactions = transactions.filter(t => ['SI', 'NSI', 'PS'].includes(t.tranTypeCode));
    const customerMap = {};
    customers.forEach(c => { customerMap[c.id] = c.companyName || c.customerCode || 'Unknown'; });

    let receivables = siTransactions.map(t => {
      const age = getAge(t.docDate);
      const amount = Number(t.balanceAmount) || 0;
      const totalAmount = Number(t.totalAmount) || 1;
      // Probability decreases with age
      const probability = Math.max(10, Math.round(100 - (age * 0.5)));
      return {
        id: t.id,
        tranTypeCode: t.tranTypeCode,
        customer: t.partyName || customerMap[t.partyId] || 'Unknown',
        invoiceNo: t.docNumber || '-',
        amount,
        expectedDate: t.dueDate || t.docDate || '',
        probability,
        age
      };
    });

    // Priority filter (map to receivables by age)
    if (selectedPriority !== 'All') {
      const prio = selectedPriority.toLowerCase();
      receivables = receivables.filter(r => {
        if (prio === 'high') return r.age > 60;
        if (prio === 'medium') return r.age > 30 && r.age <= 60;
        return r.age <= 30;
      });
    }

    return receivables;
  }, [transactions, customers, selectedPriority]);

  // Waterfall detail table data
  const waterfallDetail = useMemo(() => {
    const total = Math.abs(summary.totalInflows) + Math.abs(summary.totalOutflows) + Math.abs(summary.otherMovements);
    return [
      {
        category: 'Opening Balance',
        type: 'Total',
        amount: summary.openingCash,
        pctOfTotal: '-'
      },
      {
        category: 'Cash Receipts (CR)',
        type: 'Income',
        amount: filteredSubTransactions
          .filter(st => st.tranTypeCode === 'CR')
          .reduce((sum, st) => sum + (Number(st.debitAmount) || 0) - (Number(st.creditAmount) || 0), 0),
        pctOfTotal: total > 0 ? ((Math.abs(filteredSubTransactions
          .filter(st => st.tranTypeCode === 'CR')
          .reduce((sum, st) => sum + (Number(st.debitAmount) || 0) - (Number(st.creditAmount) || 0), 0)) / total) * 100).toFixed(1) + '%' : '0%'
      },
      {
        category: 'Bank Receipts (BR)',
        type: 'Income',
        amount: filteredSubTransactions
          .filter(st => st.tranTypeCode === 'BR')
          .reduce((sum, st) => sum + (Number(st.debitAmount) || 0) - (Number(st.creditAmount) || 0), 0),
        pctOfTotal: total > 0 ? ((Math.abs(filteredSubTransactions
          .filter(st => st.tranTypeCode === 'BR')
          .reduce((sum, st) => sum + (Number(st.debitAmount) || 0) - (Number(st.creditAmount) || 0), 0)) / total) * 100).toFixed(1) + '%' : '0%'
      },
      {
        category: 'Cash Payments (CP)',
        type: 'Expense',
        amount: filteredSubTransactions
          .filter(st => st.tranTypeCode === 'CP')
          .reduce((sum, st) => sum + (Number(st.debitAmount) || 0) - (Number(st.creditAmount) || 0), 0),
        pctOfTotal: total > 0 ? ((Math.abs(filteredSubTransactions
          .filter(st => st.tranTypeCode === 'CP')
          .reduce((sum, st) => sum + (Number(st.debitAmount) || 0) - (Number(st.creditAmount) || 0), 0)) / total) * 100).toFixed(1) + '%' : '0%'
      },
      {
        category: 'Bank Payments (BP)',
        type: 'Expense',
        amount: filteredSubTransactions
          .filter(st => st.tranTypeCode === 'BP')
          .reduce((sum, st) => sum + (Number(st.debitAmount) || 0) - (Number(st.creditAmount) || 0), 0),
        pctOfTotal: total > 0 ? ((Math.abs(filteredSubTransactions
          .filter(st => st.tranTypeCode === 'BP')
          .reduce((sum, st) => sum + (Number(st.debitAmount) || 0) - (Number(st.creditAmount) || 0), 0)) / total) * 100).toFixed(1) + '%' : '0%'
      },
      {
        category: 'Other Movements',
        type: 'Expense',
        amount: summary.otherMovements,
        pctOfTotal: total > 0 ? ((Math.abs(summary.otherMovements) / total) * 100).toFixed(1) + '%' : '0%'
      },
      {
        category: 'Closing Balance',
        type: 'Total',
        amount: summary.closingCash,
        pctOfTotal: '-'
      }
    ];
  }, [filteredSubTransactions, summary]);

  // Sorted payables
  const sortedPayables = useMemo(() => {
    const sorted = [...payablesData];
    sorted.sort((a, b) => {
      let aVal = a[payablesSortField];
      let bVal = b[payablesSortField];

      if (['amount', 'daysOverdue'].includes(payablesSortField)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return payablesSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return payablesSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [payablesData, payablesSortField, payablesSortDirection]);

  // Sorted receivables
  const sortedReceivables = useMemo(() => {
    const sorted = [...receivablesData];
    sorted.sort((a, b) => {
      let aVal = a[receivablesSortField];
      let bVal = b[receivablesSortField];

      if (['amount', 'probability', 'age'].includes(receivablesSortField)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = String(aVal || '').toLowerCase();
        bVal = String(bVal || '').toLowerCase();
      }

      if (aVal < bVal) return receivablesSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return receivablesSortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [receivablesData, receivablesSortField, receivablesSortDirection]);

  // Sort handlers
  const handlePayablesSort = (field) => {
    if (payablesSortField === field) {
      setPayablesSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setPayablesSortField(field);
      setPayablesSortDirection('asc');
    }
  };

  const handleReceivablesSort = (field) => {
    if (receivablesSortField === field) {
      setReceivablesSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setReceivablesSortField(field);
      setReceivablesSortDirection('asc');
    }
  };

  const getPayablesSortIndicator = (field) => {
    if (payablesSortField !== field) return '';
    return payablesSortDirection === 'asc' ? ' \u2191' : ' \u2193';
  };

  const getReceivablesSortIndicator = (field) => {
    if (receivablesSortField !== field) return '';
    return receivablesSortDirection === 'asc' ? ' \u2191' : ' \u2193';
  };

  // CSV Export
  const handleExport = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = 'Cash Flow Management Report\n';
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${dateFrom || 'All'} to ${dateTo || 'All'}\n`;
    csv += `Generated: ${new Date().toLocaleString()}\n\n`;

    // Summary
    csv += `Opening Balance,${summary.openingCash}\n`;
    csv += `Total Inflows,${summary.totalInflows}\n`;
    csv += `Total Outflows,${summary.totalOutflows}\n`;
    csv += `Closing Balance,${summary.closingCash}\n`;
    csv += `DSO (Days),${summary.dso}\n`;
    csv += `30-Day Forecast,${summary.forecast30}\n\n`;

    // Waterfall Detail
    csv += 'Waterfall Detail\n';
    csv += 'Category,Type,Amount,% of Total\n';
    waterfallDetail.forEach(row => {
      csv += `"${row.category}","${row.type}",${row.amount},"${row.pctOfTotal}"\n`;
    });
    csv += '\n';

    // Upcoming Payables
    csv += 'Upcoming Payables\n';
    csv += 'Vendor,Invoice #,Amount,Due Date,Days Overdue,Priority\n';
    sortedPayables.forEach(p => {
      csv += `"${(p.vendor || '').replace(/"/g, '""')}","${p.invoiceNo}",${p.amount},"${formatDateStr(p.dueDate)}",${p.daysOverdue},"${p.priority}"\n`;
    });
    csv += '\n';

    // Expected Receivables
    csv += 'Expected Receivables\n';
    csv += 'Customer,Invoice #,Amount,Expected Date,Probability %,Age\n';
    sortedReceivables.forEach(r => {
      csv += `"${(r.customer || '').replace(/"/g, '""')}","${r.invoiceNo}",${r.amount},"${formatDateStr(r.expectedDate)}",${r.probability},${r.age}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Cash_Flow_Management_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    const selectedFY = financialYears.find(f => f.id === selectedFYId);
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Cash Flow Management Report</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        h2{color:#800020;font-size:16px;margin:20px 0 10px 0}
        .meta{color:#666;font-size:11px;margin-bottom:15px}
        .summary-grid{display:flex;gap:15px;margin-bottom:20px;flex-wrap:wrap}
        .summary-box{padding:12px 18px;background:#f9f9f9;border-radius:6px;border-left:3px solid #800020;min-width:120px}
        .summary-label{font-size:10px;color:#666;text-transform:uppercase;margin-bottom:4px}
        .summary-val{font-size:16px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:7px 10px;text-align:left;font-size:10px;text-transform:uppercase}
        th.right{text-align:right}
        td{padding:6px 10px;border-bottom:1px solid #eee;font-size:11px}
        td.right{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:600}
        .totals-row td{border-top:2px solid #800020}
        .priority-high{color:#DC3545;font-weight:600}
        .priority-medium{color:#FFC107;font-weight:600}
        .priority-low{color:#28A745;font-weight:600}
        .positive{color:#28A745} .negative{color:#DC3545}
        @media print{body{margin:15px}.summary-grid{page-break-inside:avoid}}
      </style></head><body>
      <h1>Cash Flow Management Report</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${formatDateStr(dateFrom)} to ${formatDateStr(dateTo)}
      </div>
      <div class="summary-grid">
        <div class="summary-box"><div class="summary-label">Opening Balance</div><div class="summary-val">${formatAmount(summary.openingCash)}</div></div>
        <div class="summary-box"><div class="summary-label">Total Inflows</div><div class="summary-val positive">${formatAmount(summary.totalInflows)}</div></div>
        <div class="summary-box"><div class="summary-label">Total Outflows</div><div class="summary-val negative">${formatAmount(summary.totalOutflows)}</div></div>
        <div class="summary-box"><div class="summary-label">Closing Balance</div><div class="summary-val">${formatAmount(summary.closingCash)}</div></div>
        <div class="summary-box"><div class="summary-label">DSO (Days)</div><div class="summary-val">${summary.dso}</div></div>
        <div class="summary-box"><div class="summary-label">30-Day Forecast</div><div class="summary-val">${formatAmount(summary.forecast30)}</div></div>
      </div>

      <h2>Waterfall Detail</h2>
      <table>
        <thead><tr><th>Category</th><th>Type</th><th class="right">Amount</th><th class="right">% of Total</th></tr></thead>
        <tbody>
          ${waterfallDetail.map(row => `
            <tr class="${row.type === 'Total' ? 'totals-row' : ''}">
              <td>${row.category}</td>
              <td>${row.type}</td>
              <td class="right ${row.amount >= 0 ? 'positive' : 'negative'}">${formatAmount(row.amount)}</td>
              <td class="right">${row.pctOfTotal}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <h2>Upcoming Payables</h2>
      <table>
        <thead><tr><th>Vendor</th><th>Invoice #</th><th class="right">Amount</th><th>Due Date</th><th class="right">Days Overdue</th><th>Priority</th></tr></thead>
        <tbody>
          ${sortedPayables.map(p => `
            <tr>
              <td>${p.vendor}</td>
              <td>${p.invoiceNo}</td>
              <td class="right">${formatAmount(p.amount)}</td>
              <td>${formatDateStr(p.dueDate)}</td>
              <td class="right">${p.daysOverdue}</td>
              <td class="priority-${p.priority}">${p.priority.charAt(0).toUpperCase() + p.priority.slice(1)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>

      <h2>Expected Receivables</h2>
      <table>
        <thead><tr><th>Customer</th><th>Invoice #</th><th class="right">Amount</th><th>Expected Date</th><th class="right">Probability %</th><th class="right">Age</th></tr></thead>
        <tbody>
          ${sortedReceivables.map(r => `
            <tr>
              <td>${r.customer}</td>
              <td>${r.invoiceNo}</td>
              <td class="right">${formatAmount(r.amount)}</td>
              <td>${formatDateStr(r.expectedDate)}</td>
              <td class="right">${r.probability}%</td>
              <td class="right">${r.age} days</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Custom chart tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{formatCurrency(entry.value)}</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const WaterfallTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          <ChartTooltipRow>
            <span>Amount: </span>
            <strong>{formatCurrency(data?.value || 0)}</strong>
          </ChartTooltipRow>
        </ChartTooltipBox>
      );
    }
    return null;
  };

  const DSOTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <ChartTooltipBox>
          <ChartTooltipLabel>{label}</ChartTooltipLabel>
          {payload.map((entry, index) => (
            <ChartTooltipRow key={index}>
              <ChartTooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>{entry.value} days</strong>
            </ChartTooltipRow>
          ))}
        </ChartTooltipBox>
      );
    }
    return null;
  };

  // Payables totals
  const payablesTotals = useMemo(() => {
    return sortedPayables.reduce((sum, p) => sum + p.amount, 0);
  }, [sortedPayables]);

  // Receivables totals
  const receivablesTotals = useMemo(() => {
    return sortedReceivables.reduce((sum, r) => sum + r.amount, 0);
  }, [sortedReceivables]);

  return (
    <PageLayout pageTitle="Cash Flow Management Report" breadcrumb="Reports / Financial Reports / Cash Flow Management">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={e => setSelectedFYId(e.target.value)}>
              <option value="">Select Financial Year</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
          </FilterGroup>

          <FilterGroup>
            <Label>Priority</Label>
            <Select value={selectedPriority} onChange={e => setSelectedPriority(e.target.value)}>
              <option value="All">All Priorities</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </Select>
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={!hasSearched}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={!hasSearched}>
            <FiPrinter /> Print
          </Button>
          <Button $primary onClick={loadData} disabled={loading || !selectedFYId}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards */}
      {hasSearched && !loading && (
        <SummaryCards>
          <SummaryCard>
            <SummaryIcon $color={colors.chart.primary}><FiDollarSign /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Opening Balance</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.openingCash)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#28A745"><FiTrendingUp /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Inflows</SummaryLabel>
              <SummaryValue style={{ color: '#28A745' }}>{formatCurrency(summary.totalInflows)}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color="#DC3545"><FiTrendingDown /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Outflows</SummaryLabel>
              <SummaryValue style={{ color: '#DC3545' }}>{formatCurrency(Math.abs(summary.totalOutflows))}</SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.primary.main}><FiActivity /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Closing Balance</SummaryLabel>
              <SummaryValue style={{ color: summary.closingCash >= 0 ? '#28A745' : '#DC3545' }}>
                {formatCurrency(summary.closingCash)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent3}><FiClock /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>DSO (Days)</SummaryLabel>
              <SummaryValue style={{ color: summary.dso > 30 ? '#DC3545' : '#28A745' }}>
                {summary.dso} days
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>

          <SummaryCard>
            <SummaryIcon $color={colors.chart.accent1}><FiTarget /></SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>30-Day Forecast</SummaryLabel>
              <SummaryValue style={{ color: summary.forecast30 >= 0 ? '#28A745' : '#DC3545' }}>
                {formatCurrency(summary.forecast30)}
              </SummaryValue>
            </SummaryInfo>
          </SummaryCard>
        </SummaryCards>
      )}

      {/* Upcoming Payables Table */}
      <ContentCard>
        <SectionHeader>Upcoming Payables</SectionHeader>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyIcon><FiDollarSign /></EmptyIcon>
              <EmptyText>Loading cash flow data...</EmptyText>
            </EmptyState>
          ) : !hasSearched ? (
            <EmptyState>
              <EmptyIcon><FiDollarSign /></EmptyIcon>
              <EmptyText>Select filters and click "Generate Report" to view cash flow management</EmptyText>
            </EmptyState>
          ) : sortedPayables.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiCalendar /></EmptyIcon>
              <EmptyText>No upcoming payables found for the selected criteria</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th $sortable onClick={() => handlePayablesSort('vendor')}>Vendor{getPayablesSortIndicator('vendor')}</Th>
                  <Th $sortable onClick={() => handlePayablesSort('invoiceNo')}>Invoice #{getPayablesSortIndicator('invoiceNo')}</Th>
                  <Th $sortable $right onClick={() => handlePayablesSort('amount')}>Amount{getPayablesSortIndicator('amount')}</Th>
                  <Th $sortable onClick={() => handlePayablesSort('dueDate')}>Due Date{getPayablesSortIndicator('dueDate')}</Th>
                  <Th $sortable $right onClick={() => handlePayablesSort('daysOverdue')}>Days Overdue{getPayablesSortIndicator('daysOverdue')}</Th>
                  <Th $sortable onClick={() => handlePayablesSort('priority')}>Priority{getPayablesSortIndicator('priority')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedPayables.map((p, index) => (
                  <Tr key={p.id || index}>
                    <Td>{p.vendor}</Td>
                    <Td>{p.invoiceNo !== '-' && TRAN_TYPE_ROUTES[p.tranTypeCode] && p.id ? (
                      <span onClick={() => handleDocClick(p)} title="Open invoice" style={{ color: colors.primary.main, cursor: 'pointer', fontWeight: 500 }}>{p.invoiceNo}</span>
                    ) : p.invoiceNo}</Td>
                    <AmountCell $right>{formatAmount(p.amount)}</AmountCell>
                    <Td>{formatDateStr(p.dueDate)}</Td>
                    <AmountCell $right style={{ color: p.daysOverdue > 0 ? colors.error.main : colors.text.primary }}>
                      {p.daysOverdue}
                    </AmountCell>
                    <Td>
                      <PriorityBadge $priority={p.priority}>
                        {PRIORITY_CONFIG[p.priority]?.label || p.priority}
                      </PriorityBadge>
                    </Td>
                  </Tr>
                ))}

                <TotalsRow>
                  <Td colSpan={2} style={{ textAlign: 'right', fontWeight: 700 }}>
                    Totals ({sortedPayables.length} records)
                  </Td>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(payablesTotals)}</AmountCell>
                  <Td colSpan={3}></Td>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Expected Receivables Table */}
      <ContentCard>
        <SectionHeader>Expected Receivables</SectionHeader>
        <TableContainer>
          {hasSearched && !loading && sortedReceivables.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiCalendar /></EmptyIcon>
              <EmptyText>No expected receivables found for the selected criteria</EmptyText>
            </EmptyState>
          ) : hasSearched && !loading ? (
            <Table>
              <Thead>
                <Tr>
                  <Th $sortable onClick={() => handleReceivablesSort('customer')}>Customer{getReceivablesSortIndicator('customer')}</Th>
                  <Th $sortable onClick={() => handleReceivablesSort('invoiceNo')}>Invoice #{getReceivablesSortIndicator('invoiceNo')}</Th>
                  <Th $sortable $right onClick={() => handleReceivablesSort('amount')}>Amount{getReceivablesSortIndicator('amount')}</Th>
                  <Th $sortable onClick={() => handleReceivablesSort('expectedDate')}>Expected Date{getReceivablesSortIndicator('expectedDate')}</Th>
                  <Th $sortable $right onClick={() => handleReceivablesSort('probability')}>Probability %{getReceivablesSortIndicator('probability')}</Th>
                  <Th $sortable $right onClick={() => handleReceivablesSort('age')}>Age{getReceivablesSortIndicator('age')}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {sortedReceivables.map((r, index) => (
                  <Tr key={r.id || index}>
                    <Td>{r.customer}</Td>
                    <Td>{r.invoiceNo !== '-' && TRAN_TYPE_ROUTES[r.tranTypeCode] && r.id ? (
                      <span onClick={() => handleDocClick(r)} title="Open invoice" style={{ color: colors.primary.main, cursor: 'pointer', fontWeight: 500 }}>{r.invoiceNo}</span>
                    ) : r.invoiceNo}</Td>
                    <AmountCell $right>{formatAmount(r.amount)}</AmountCell>
                    <Td>{formatDateStr(r.expectedDate)}</Td>
                    <AmountCell $right>
                      <ProbabilityBar>
                        <ProbabilityFill $pct={r.probability} />
                        <ProbabilityText>{r.probability}%</ProbabilityText>
                      </ProbabilityBar>
                    </AmountCell>
                    <Td style={{ textAlign: 'right' }}>
                      <AgeBadge $age={r.age}>{r.age} days</AgeBadge>
                    </Td>
                  </Tr>
                ))}

                <TotalsRow>
                  <Td colSpan={2} style={{ textAlign: 'right', fontWeight: 700 }}>
                    Totals ({sortedReceivables.length} records)
                  </Td>
                  <AmountCell $right style={{ fontWeight: 700 }}>{formatAmount(receivablesTotals)}</AmountCell>
                  <Td colSpan={3}></Td>
                </TotalsRow>
              </Tbody>
            </Table>
          ) : null}
        </TableContainer>
      </ContentCard>

      {/* Waterfall Detail Table */}
      {hasSearched && !loading && (
        <ContentCard>
          <SectionHeader>Waterfall Detail</SectionHeader>
          <TableContainer>
            <Table>
              <Thead>
                <Tr>
                  <Th>Category</Th>
                  <Th>Type</Th>
                  <Th $right>Amount</Th>
                  <Th $right>% of Total</Th>
                </Tr>
              </Thead>
              <Tbody>
                {waterfallDetail.map((row, index) => (
                  <Tr key={index} style={row.type === 'Total' ? { background: colors.neutral[100], fontWeight: 600 } : {}}>
                    <Td style={row.type === 'Total' ? { fontWeight: 700 } : {}}>{row.category}</Td>
                    <Td>
                      <TypeBadge $type={row.type}>{row.type}</TypeBadge>
                    </Td>
                    <AmountCell $right style={{
                      color: row.amount >= 0 ? (row.type === 'Income' ? '#28A745' : colors.text.primary) : '#DC3545',
                      fontWeight: row.type === 'Total' ? 700 : 400
                    }}>
                      {row.amount < 0 ? '(' : ''}{formatAmount(Math.abs(row.amount))}{row.amount < 0 ? ')' : ''}
                    </AmountCell>
                    <AmountCell $right>{row.pctOfTotal}</AmountCell>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </ContentCard>
      )}

      {/* Charts */}
      {hasSearched && !loading && (
        <ChartsRow>
          {/* Waterfall BarChart */}
          <ChartSection $wide>
            <ChartTitle>Cash Flow Waterfall</ChartTitle>
            {waterfallData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={waterfallData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <YAxis tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => formatNumber(v, { compact: true })} />
                  <Tooltip content={<WaterfallTooltip />} />
                  <Bar dataKey="base" stackId="waterfall" fill="transparent" />
                  <Bar dataKey="value" stackId="waterfall" radius={[4, 4, 0, 0]} barSize={50}>
                    {waterfallData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={entry.value >= 0 ? (entry.name === 'Closing' || entry.name === 'Opening' ? CHART_COLORS[0] : colors.success.main) : colors.error.main}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No waterfall data available</ChartEmpty>
            )}
          </ChartSection>

          {/* Cash Forecast AreaChart */}
          <ChartSection>
            <ChartTitle>Cash Forecast (30/60/90 Days)</ChartTitle>
            {forecastData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={forecastData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="projectedGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={colors.primary.main} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={colors.primary.main} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="optimisticGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#28A745" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#28A745" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="conservativeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#DC3545" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#DC3545" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <YAxis tick={{ fontSize: 11 }} stroke={colors.text.secondary} tickFormatter={(v) => formatNumber(v, { compact: true })} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Area type="monotone" dataKey="optimistic" name="Optimistic" stroke="#28A745" fill="url(#optimisticGradient)" strokeWidth={1.5} strokeDasharray="5 5" />
                  <Area type="monotone" dataKey="projected" name="Projected" stroke={colors.primary.main} fill="url(#projectedGradient)" strokeWidth={2} />
                  <Area type="monotone" dataKey="conservative" name="Conservative" stroke="#DC3545" fill="url(#conservativeGradient)" strokeWidth={1.5} strokeDasharray="5 5" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No forecast data available</ChartEmpty>
            )}
          </ChartSection>

          {/* DSO Trend LineChart */}
          <ChartSection>
            <ChartTitle>DSO Trend (Monthly)</ChartTitle>
            {dsoTrendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dsoTrendData} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <YAxis tick={{ fontSize: 11 }} stroke={colors.text.secondary} />
                  <Tooltip content={<DSOTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <ReferenceLine y={30} stroke={colors.warning.main} strokeDasharray="5 5" label={{ value: 'Target: 30 days', position: 'right', fontSize: 10, fill: colors.warning.main }} />
                  <Line type="monotone" dataKey="dso" name="DSO" stroke={colors.primary.main} strokeWidth={2} dot={{ r: 4, fill: colors.primary.main }} activeDot={{ r: 6 }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <ChartEmpty>No DSO trend data available</ChartEmpty>
            )}
          </ChartSection>
        </ChartsRow>
      )}
    </PageLayout>
  );
};

// ============ STYLED COMPONENTS ============

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const Select = styled.select`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 9px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 9px 18px;
  border: ${props => props.$primary ? 'none' : `1px solid ${colors.neutral[300]}`};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: all 0.2s;

  ${props => props.$primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover { background: ${colors.primary.dark}; }
  ` : `
    background: white;
    color: ${colors.text.primary};
    &:hover { background: ${colors.neutral[100]}; border-color: ${colors.primary.main}; }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.$color || colors.primary.main}15;
  color: ${props => props.$color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
  white-space: nowrap;
`;

const SummaryValue = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const ChartSection = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;

  ${props => props.$wide && `
    grid-column: 1 / -1;
  `}
`;

const ChartTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const ChartEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${colors.text.tertiary};
  font-size: 14px;
`;

const ChartTooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 10px 14px;
  box-shadow: ${colors.shadows.md};
`;

const ChartTooltipLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 6px;
`;

const ChartTooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 2px;

  strong {
    color: ${colors.text.primary};
  }
`;

const ChartTooltipDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
  margin-bottom: 20px;
`;

const SectionHeader = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.text.primary};
  padding: 16px 20px 0 20px;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 14px;
  text-align: ${props => props.$right ? 'right' : 'left'};
  font-size: 11px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid ${colors.neutral[200]};
  white-space: nowrap;
  user-select: none;

  ${props => props.$sortable && `
    cursor: pointer;
    transition: color 0.2s;
    &:hover {
      color: ${colors.primary.main};
    }
  `}
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.15s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.primary.main};

  td {
    padding: 14px;
  }

  &:hover {
    background: ${colors.neutral[100]};
  }
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 13px;
  color: ${colors.text.primary};
  text-align: ${props => props.$right ? 'right' : 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', 'Courier New', monospace;
  font-size: 13px;
  text-align: right;
`;

const PriorityBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => PRIORITY_CONFIG[props.$priority]?.bg || colors.neutral[100]};
  color: ${props => PRIORITY_CONFIG[props.$priority]?.color || colors.text.secondary};
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => {
    if (props.$type === 'Income') return '#E8F5E9';
    if (props.$type === 'Expense') return '#FFEBEE';
    return colors.neutral[100];
  }};
  color: ${props => {
    if (props.$type === 'Income') return '#28A745';
    if (props.$type === 'Expense') return '#DC3545';
    return colors.text.primary;
  }};
`;

const ProbabilityBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
`;

const ProbabilityFill = styled.div`
  width: 50px;
  height: 6px;
  border-radius: 3px;
  background: ${colors.neutral[200]};
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${props => props.$pct || 0}%;
    background: ${props => {
      const pct = props.$pct || 0;
      if (pct >= 70) return colors.success.main;
      if (pct >= 40) return colors.warning.main;
      return colors.error.main;
    }};
    border-radius: 3px;
    transition: width 0.3s ease;
  }
`;

const ProbabilityText = styled.span`
  font-size: 12px;
  font-weight: 500;
  min-width: 35px;
  text-align: right;
`;

const AgeBadge = styled.span`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  background: ${props => {
    const age = props.$age || 0;
    if (age > 60) return '#FFEBEE';
    if (age > 30) return '#FFF3E0';
    return '#E8F5E9';
  }};
  color: ${props => {
    const age = props.$age || 0;
    if (age > 60) return colors.error.main;
    if (age > 30) return colors.warning.main;
    return colors.success.main;
  }};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default CashFlowManagementReport;
